import { ArrowBack, ArrowForward, ExpandMore } from '@mui/icons-material/';
import {
  Apps,
  WorkOutline,
  DeviceHub,
  Business,
  AccountTree,
  Extension,
  PeopleAlt,
  Computer,
  AdminPanelSettings,
  PlaylistAddCheck,
  HourglassFull,
  EventNote,
  Settings,
} from '@mui/icons-material/';
import { Accordion, AccordionSummary, AccordionDetails, ListItemIcon, ListItemText } from '@mui/material/';
import { LogoKappaWhite, LogoKappaWhiteRounded } from 'Images/index';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useTranslation from 'Hooks/useTranslation';
import { useSelector } from 'react-redux';
import { userSelector } from 'Redux/LoggedUserSlice';
import rolesApi from 'Api/axios/roles-api';
import { SESSION_PERMISSIONS, useHasPermission, checkPermission } from 'Utils/permissions';
import settingsSections from 'Pages/Settings/data/sections';
import { usePermissionStore } from 'stores/permissions-store';
import { useDrawerStore } from 'stores/drawer-store';
import { colors } from 'Constants/Colors';
import styled from '@emotion/styled';

const DRAWER_WIDTH_OPEN = 232;
const DRAWER_WIDTH_CLOSED = 72;

// Tokens semánticos del menú lateral (tema oscuro).
// Centralizados aquí para que cualquier ajuste de marca cambie en un solo punto.
const sidebar = {
  bg: colors.BLACK, // #262626
  border: 'rgba(255, 255, 255, 0.08)',
  text: colors.WHITE,
  textIconMuted: colors.GREEN, // verde de marca para íconos en reposo
  hoverBg: 'rgba(0, 152, 74, 0.18)',
  selectedBg: colors.GREEN,
  selectedText: colors.WHITE,
  subSelectedBg: 'rgba(0, 152, 74, 0.22)',
  subSelectedText: colors.WHITE,
  subSelectedBorder: colors.GREEN,
} as const;

const StyledDrawer = styled.aside<{ $open: boolean }>(({ $open }) => ({
  width: $open ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  background: sidebar.bg,
  borderRight: `1px solid ${sidebar.border}`,
  transition: 'width 0.3s ease',
  height: '100vh',
  position: 'sticky',
  top: 0,
  alignSelf: 'flex-start',
  overflowX: 'hidden',
  overflowY: 'auto',
}));

const DrawerHeader = styled.div<{ $open: boolean }>(({ $open }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: $open ? '28px 24px 22px' : '20px 8px',
}));

const HeaderLogo = styled.img<{ $open: boolean }>(({ $open }) => ({
  width: $open ? '100%' : '60%',
  height: 'auto',
  display: 'block',
}));

const Nav = styled.nav({
  flex: 1,
  padding: '8px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

const Footer = styled.div({
  padding: '14px 16px 22px',
  borderTop: `1px solid ${sidebar.border}`,
});

// Item base del menú (nivel 1). `as="div"` cuando se usa dentro del Accordion
// para evitar botón anidado en un botón.
const MenuItemButton = styled.button<{ $isSelected: boolean }>(({ $isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  padding: '11px 14px',
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--app-font)',
  fontSize: 13.5,
  textAlign: 'left',
  background: $isSelected ? sidebar.selectedBg : 'transparent',
  color: $isSelected ? sidebar.selectedText : sidebar.text,
  fontWeight: $isSelected ? 600 : 500,
  transition: 'background 0.15s ease, color 0.15s ease',
  '& .MuiListItemIcon-root': {
    minWidth: 'auto',
    color: $isSelected ? sidebar.selectedText : sidebar.textIconMuted,
  },
  '& .MuiListItemIcon-root .MuiSvgIcon-root': {
    fontSize: 20,
  },
  '& .MuiListItemText-root': {
    flex: '1 1 auto',
    minWidth: 0,
    margin: 0,
  },
  '& .MuiTypography-root': {
    fontFamily: 'var(--app-font)',
    fontSize: 13.5,
    fontWeight: 'inherit',
    color: 'inherit',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  '&:hover': !$isSelected
    ? {
        background: sidebar.hoverBg,
      }
    : undefined,
}));

const SubMenuItem = styled.button<{ $isSelected: boolean }>(({ $isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '9px 12px',
  borderRadius: 6,
  border: 'none',
  borderLeft: `2.5px solid ${$isSelected ? sidebar.subSelectedBorder : 'transparent'}`,
  cursor: 'pointer',
  fontFamily: 'var(--app-font)',
  fontSize: 12.5,
  textAlign: 'left',
  background: $isSelected ? sidebar.subSelectedBg : 'transparent',
  color: $isSelected ? sidebar.subSelectedText : sidebar.text,
  fontWeight: $isSelected ? 600 : 500,
  transition: 'background 0.15s ease, color 0.15s ease',
  '& .MuiSvgIcon-root': {
    fontSize: 16,
    color: $isSelected ? sidebar.subSelectedText : sidebar.textIconMuted,
  },
  '&:hover': !$isSelected
    ? {
        background: sidebar.hoverBg,
      }
    : undefined,
}));

const ReportsAccordion = styled(Accordion)({
  background: 'transparent',
  boxShadow: 'none',
  margin: '0 !important',
  '&:before': { display: 'none' },
  '& .MuiAccordionSummary-root': { padding: 0, minHeight: 'auto' },
  '& .MuiAccordionSummary-content': { margin: 0 },
  '& .MuiAccordionDetails-root': {
    padding: '4px 0 0 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
});

const SubMenuLabel = styled.span({ flex: '1 1 auto' });

const FooterButton = styled.button({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: 0,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--app-font)',
  fontSize: 13,
  color: sidebar.text,
  '& .MuiSvgIcon-root': {
    fontSize: 20,
    color: sidebar.selectedBg,
  },
});

export const Drawer = () => {
  const history = useHistory();
  const open = useDrawerStore(state => state.open);
  const toggleOpen = useDrawerStore(state => state.toggle);
  const { t2 } = useTranslation('SideBar');
  const { roleId } = useSelector(userSelector);

  // Store de permisos crudos; el mapeo y ordenado se hace aquí
  const rawPermissions: any[] = usePermissionStore(state => state.permissions);
  const setPermissions = usePermissionStore(state => state.setPermissions);
  const canReadSessionsReport = useHasPermission(SESSION_PERMISSIONS.REPORT_READ);
  // El menú "Reportes" agrupa dos familias de permisos: `report.*` (reportes
  // generales) y `session.report.read` (reporte de sesiones). Las pintamos por
  // separado para que un rol con solo uno de los dos aún vea el padre.
  const canReadGeneralReports = useHasPermission('report.read');

  // El apartado de Configuración solo se muestra si el usuario puede acceder a
  // al menos una sección interna disponible (p. ej. Instructores). El permiso de
  // cada sección vive en `settingsSections`, una sola fuente de verdad.
  const canSeeSettings = useMemo(
    () =>
      settingsSections.some(
        section =>
          section.available &&
          (!section.requiredPermission || checkPermission(rawPermissions, section.requiredPermission))
      ),
    [rawPermissions]
  );

  // Estado para controlar el acordeón de Reportes
  const [reportsExpanded, setReportsExpanded] = useState(false);

  /**
   * Representa un subitem dentro de un menú con acordeón.
   */
  type SubMenuItem = { route: string; textKey: string; icon: React.ReactNode };

  /**
   * Representa un item renderizable dentro del Drawer.
   * - icon: nodo React del icono a mostrar.
   * - route: ruta a navegar al hacer clic (vacío si tiene subItems).
   * - text: etiqueta ya traducida a mostrar.
   * - subItems: lista opcional de subitems para menús acordeón.
   */
  type MenuItem = {
    icon: React.ReactNode;
    route: string;
    text: string;
    subItems?: { route: string; text: string; icon: React.ReactNode }[];
  };

  /**
   * Configuración de cada opción del menú.
   * - id: prefijo de permiso (o id lógico para items sin permiso).
   * - route: ruta de navegación (vacío si tiene subItems).
   * - icon: icono a mostrar.
   * - textKey: clave de traducción para SideBar.json.
   * - requiresPermission: si true, se mostrará solo si el backend asignó el permiso correspondiente (id).
   * - order: prioridad de orden ascendente. Menor número aparece antes.
   * - subItems: lista opcional de subitems para menús acordeón.
   * - visible: si se define en false, oculta el item sin importar el permiso. Útil
   *   para gating calculado fuera del esquema `id`-permiso (p. ej. Configuración).
   */
  type MenuConfig = {
    id: string;
    route: string;
    icon: React.ReactNode;
    textKey: string;
    requiresPermission: boolean;
    order: number;
    subItems?: SubMenuItem[];
    visible?: boolean;
  };

  // Config legible con control por "order".
  const menuConfig: MenuConfig[] = useMemo(
    () => [
      // Panel Principal: siempre arriba de todos (order más bajo)
      {
        id: 'dashboard',
        route: '/dashboard',
        icon: <Apps />,
        textKey: 'Dashboard',
        requiresPermission: false,
        order: 0,
      },
      // Preferidos al inicio
      {
        id: 'employee_development',
        route: '/empleado/me',
        icon: <PeopleAlt />,
        textKey: 'Profile',
        requiresPermission: false,
        order: 10,
      },
      {
        id: 'my_courses',
        route: '/mis-cursos',
        icon: <Computer />,
        textKey: 'MyCourses',
        requiresPermission: false,
        order: 20,
      },
      // Resto de módulos
      {
        id: 'job_title',
        route: '/puestos',
        icon: <WorkOutline />,
        textKey: 'JobTitle',
        requiresPermission: true,
        order: 40,
      },
      {
        id: 'department',
        route: '/departamentos',
        icon: <DeviceHub />,
        textKey: 'Departments',
        requiresPermission: true,
        order: 50,
      },
      {
        id: 'enterprise',
        route: '/empresa',
        icon: <Business />,
        textKey: 'Company',
        requiresPermission: true,
        order: 60,
      },
      {
        id: 'course_definition',
        route: '/definicion',
        icon: <AccountTree />,
        textKey: 'DefinitionCourses',
        requiresPermission: true,
        order: 70,
      },
      {
        id: 'report',
        route: '',
        icon: <Extension />,
        textKey: 'reports',
        // La visibilidad del padre se decide en el filtro de abajo: se muestra
        // si su lista de subItems quedó con al menos un elemento.
        requiresPermission: false,
        order: 80,
        subItems: [
          ...(canReadGeneralReports
            ? [
                { route: '/reporte/empleados', textKey: 'reportsCurrent', icon: <PlaylistAddCheck /> },
                { route: '/reporte/historial/empleados', textKey: 'reportsHistory', icon: <HourglassFull /> },
              ]
            : []),
          ...(canReadSessionsReport
            ? [{ route: '/reporte/sesiones/cursos', textKey: 'reportsSessions', icon: <EventNote /> }]
            : []),
        ],
      },
      {
        id: 'employee',
        route: '/empleados',
        icon: <PeopleAlt />,
        textKey: 'Employees',
        requiresPermission: true,
        order: 90,
      },
      {
        id: 'course',
        route: '/cursos',
        icon: <Computer />,
        textKey: 'Courses',
        requiresPermission: true,
        order: 100,
      },
      {
        id: 'roles_permissions',
        route: '/roles',
        icon: <AdminPanelSettings />,
        textKey: 'Management',
        requiresPermission: true,
        order: 110,
      },
      {
        id: 'settings',
        route: '/configuracion',
        icon: <Settings />,
        textKey: 'Settings',
        requiresPermission: false,
        order: 120,
        visible: canSeeSettings,
      },
    ],
    [canReadSessionsReport, canReadGeneralReports, canSeeSettings]
  );

  /**
   * Construye la lista final de ítems visibles según permisos y orden.
   * - Siempre incluye el Panel Principal primero (order=0).
   * - Si aún no llegan permisos, muestra Perfil + Mis cursos + Panel Principal.
   */
  const menuItems: MenuItem[] = useMemo(() => {
    const assigned = new Set(
      (rawPermissions || [])
        .filter(p => p?.assigned)
        .map(p => (typeof p.key === 'string' ? p.key.split('.')[0] : ''))
        .filter(Boolean)
    );

    const mapConfigToMenuItem = (cfg: MenuConfig): MenuItem => ({
      icon: cfg.icon,
      route: cfg.route,
      text: t2(cfg.textKey),
      subItems: cfg.subItems?.map(sub => ({
        route: sub.route,
        text: t2(sub.textKey),
        icon: sub.icon,
      })),
    });

    // Si permisos aún no cargan: mostrar Perfil + Mis cursos + Panel Principal
    if (!rawPermissions?.length) {
      return menuConfig
        .filter(cfg => cfg.id === 'dashboard' || cfg.id === 'my_courses' || cfg.id === 'employee_development')
        .sort((a, b) => a.order - b.order)
        .map(mapConfigToMenuItem);
    }

    // Filtrar por permisos y ordenar por prioridad.
    // - Items con permiso directo (requiresPermission): visibles si el prefijo está asignado.
    // - Items con acordeón (subItems): visibles si conservan al menos un subitem.
    return menuConfig
      .filter(cfg => {
        if (cfg.visible === false) return false;
        if (cfg.subItems) return cfg.subItems.length > 0;
        if (cfg.requiresPermission) return assigned.has(cfg.id);
        return true;
      })
      .sort((a, b) => a.order - b.order)
      .map(mapConfigToMenuItem);
  }, [rawPermissions, menuConfig, t2]);

  /**
   * Carga de permisos del rol actual para decidir qué módulos se muestran.
   * Guardamos los permisos crudos en el store global y el mapeo a menú se
   * realiza en este componente.
   */
  useEffect(() => {
    if (!roleId) return;
    rolesApi.readPermissions(roleId).then(response => {
      if (response.data) {
        // Guardamos los permisos crudos; el mapeo a menú se hace localmente
        setPermissions(response.data.permissions);
      }
    });
  }, [roleId]);

  /**
   * Determina si la ruta dada debe mostrarse como seleccionada en el menú.
   * Considera coincidencia exacta o con subrutas.
   */
  const isRouteSelected = (route: string): boolean => {
    const currentPath = history.location.pathname;

    // Caso especial: Reportes Actuales incluye tanto /reporte/empleados como /reporte/cursos
    if (route === '/reporte/empleados') {
      return (
        currentPath === '/reporte/empleados' ||
        currentPath.startsWith('/reporte/empleados/') ||
        currentPath === '/reporte/cursos' ||
        currentPath.startsWith('/reporte/cursos/')
      );
    }

    // Caso especial: Reportes Historial incluye tanto /reporte/historial/empleados como /reporte/historial/cursos
    if (route === '/reporte/historial/empleados') {
      return (
        currentPath === '/reporte/historial/empleados' ||
        currentPath.startsWith('/reporte/historial/empleados/') ||
        currentPath === '/reporte/historial/cursos' ||
        currentPath.startsWith('/reporte/historial/cursos/')
      );
    }

    return currentPath === route || currentPath.startsWith(`${route}/`);
  };

  /**
   * Verifica si alguna subruta de reportes está activa para expandir el acordeón.
   */
  const isReportsActive = (): boolean => {
    const currentPath = history.location.pathname;
    return currentPath.startsWith('/reporte/');
  };

  /**
   * Render principal del Drawer (menú lateral).
   */
  return (
    <StyledDrawer $open={open}>
      <DrawerHeader $open={open}>
        <HeaderLogo $open={open} src={open ? LogoKappaWhite : LogoKappaWhiteRounded} alt="Kappa" />
      </DrawerHeader>

      <Nav>
        {menuItems.map(item => {
          // Si el item tiene subItems, renderizar como acordeón
          if (item.subItems && item.subItems.length > 0) {
            const isAnySubItemSelected = isReportsActive();
            return (
              <ReportsAccordion
                key={item.text}
                expanded={open && (reportsExpanded || isAnySubItemSelected)}
                onChange={() => setReportsExpanded(!reportsExpanded)}
                disableGutters
              >
                <AccordionSummary
                  expandIcon={
                    open ? (
                      <ExpandMore
                        sx={{
                          color: isAnySubItemSelected ? sidebar.selectedText : sidebar.textIconMuted,
                          fontSize: 18,
                        }}
                      />
                    ) : null
                  }
                  sx={{ padding: 0, minHeight: 'auto' }}
                >
                  <MenuItemButton
                    as="div"
                    $isSelected={isAnySubItemSelected}
                    onClick={e => {
                      if (!open) {
                        // Si el drawer está cerrado, navegar al primer subitem
                        history.push(item.subItems![0].route);
                        e.stopPropagation();
                      }
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    {open && <ListItemText primary={item.text} />}
                  </MenuItemButton>
                </AccordionSummary>
                {open && (
                  <AccordionDetails>
                    {item.subItems.map(subItem => (
                      <SubMenuItem
                        key={subItem.route}
                        $isSelected={isRouteSelected(subItem.route)}
                        onClick={() => history.push(subItem.route)}
                      >
                        {subItem.icon}
                        <SubMenuLabel>{subItem.text}</SubMenuLabel>
                      </SubMenuItem>
                    ))}
                  </AccordionDetails>
                )}
              </ReportsAccordion>
            );
          }

          // Item normal sin submenú
          const isSelected = isRouteSelected(item.route);
          return (
            <MenuItemButton key={item.text} $isSelected={isSelected} onClick={() => history.push(item.route)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              {open && <ListItemText primary={item.text} />}
            </MenuItemButton>
          );
        })}
      </Nav>

      <Footer>
        <FooterButton onClick={toggleOpen} aria-label={t2('HideMenu')}>
          {open ? <ArrowBack /> : <ArrowForward />}
          {open && <span>{t2('HideMenu')}</span>}
        </FooterButton>
      </Footer>
    </StyledDrawer>
  );
};
